import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * MyRecoveryTasksPage - Team-Based Recovery Task Management
 * Displays recovery tasks assigned to IT, DevOps, Facility, and Vendor teams
 * 
 * KEY LOGIC:
 * - If IT dependency is hosted on PUBLIC CLOUD → DevOps Team of Business Process owns recovery
 * - If IT dependency is hosted elsewhere → IT Team owns recovery
 * - Each team creates their own recovery strategy following their OWN BIA's RTO/RPO
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { RecoveryTaskTable } from "../components/tables/RecoveryTaskTable";
import {
  ClipboardList,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Play,
  Send,
  Server,
  Building2,
  Truck,
  Cloud,
  Target,
  HelpCircle,
  Plus,
  Trash2,
  ChevronRight,
  ArrowRight,
  Zap,
  Timer,
  Snowflake,
  Wrench,
  RefreshCw,
  FileText,
  Info,
} from "lucide-react";

// Recovery strategy options
const RECOVERY_STRATEGIES = [
  { 
    id: "hot_standby", 
    name: "Hot Standby", 
    rtoRange: "0-4 hours",
    icon: Zap,
    color: "bg-red-100 text-red-700 border-red-300",
    description: "Active-Active or Active-Passive. Immediate failover capability.",
    cost: "High"
  },
  { 
    id: "warm_standby", 
    name: "Warm Standby", 
    rtoRange: "4-24 hours",
    icon: Timer,
    color: "bg-amber-100 text-amber-700 border-amber-300",
    description: "Pre-configured standby systems. Requires activation and data sync.",
    cost: "Medium"
  },
  { 
    id: "cold_standby", 
    name: "Cold Standby", 
    rtoRange: "24-72 hours",
    icon: Snowflake,
    color: "bg-blue-100 text-blue-700 border-blue-300",
    description: "Rebuild from backups. Manual intervention required.",
    cost: "Low"
  },
  { 
    id: "manual_workaround", 
    name: "Manual Workaround", 
    rtoRange: "Varies",
    icon: Wrench,
    color: "bg-slate-100 text-slate-700 border-slate-300",
    description: "Business operates without technology. Paper-based or alternative processes.",
    cost: "Minimal"
  },
];

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  in_progress: "bg-blue-100 text-blue-700",
  submitted: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  revision_required: "bg-sky-100 text-sky-700",
};

const TEAM_ICONS = {
  it_team: Server,
  devops_team: Cloud,
  facility_team: Building2,
  vendor_team: Truck,
  business_process_owner: ClipboardList,
};

const TEAM_NAMES = {
  it_team: "IT-DRP (IT Disaster Recovery)",
  devops_team: "IT-DRP (DevOps Cloud Recovery)",
  facility_team: "BCP (Facility Recovery)",
  vendor_team: "BCP (Vendor Recovery)",
  business_process_owner: "BCP (Business Continuity Plan)",
};

const TEAM_COLORS = {
  it_team: "bg-blue-100",
  devops_team: "bg-cyan-100",
  facility_team: "bg-purple-100",
  vendor_team: "bg-sky-100",
  business_process_owner: "bg-emerald-100",
};

const PLAN_TYPES = {
  it_drp: { name: "IT-DRP", color: "bg-blue-100 text-blue-700", description: "IT Disaster Recovery Plan" },
  bcp: { name: "BCP", color: "bg-emerald-100 text-emerald-700", description: "Business Continuity Plan" },
};

export default function MyRecoveryTasksPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ pending: 0, in_progress: 0, submitted: 0 });
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showWizardDialog, setShowWizardDialog] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardQuestions, setWizardQuestions] = useState([]);
  const [wizardAnswers, setWizardAnswers] = useState({});
  const [recommendation, setRecommendation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Approver selection state
  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedApprover, setSelectedApprover] = useState(null);
  
  // Recovery plan form data - New format with Task ID and Predecessor
  const [recoveryPlan, setRecoveryPlan] = useState({
    plan_type: "it_drp",  // it_drp or bcp
    recovery_strategy: {
      strategy_type: "",
      target_rto_hours: 0,
      planned_rto_hours: 0,
      target_rpo_hours: 0,
      planned_rpo_hours: 0,
      justification: "",
      cost_estimate: "medium",
    },
    // New format: structured tasks with Task ID and Predecessor
    recovery_tasks: [],
    // Legacy format kept for backwards compatibility
    recovery_steps: [],
    prerequisites: "",
    estimated_recovery_hours: 0,
    resource_requirements: "",
    validation_steps: [],
    notes: "",
    // BCP-specific fields
    alternate_work_location: "",
    essential_personnel: [],
    communication_plan: "",
  });
  
  // New task format: Task ID, Actual Task, Task Owner, Predecessor Task ID, Estimated Duration, Notes
  const [newTask, setNewTask] = useState({
    task_id: "",
    actual_task: "",
    task_owner: "",
    predecessor_task_id: "",
    estimated_duration: "30 mins",
    estimated_duration_mins: 30,
    notes: "",
  });
  const [newStep, setNewStep] = useState({ action: "", responsible: "", duration_mins: 30, notes: "" });
  const [newValidation, setNewValidation] = useState("");

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/recovery-tasks/my-tasks");
      setTasks(response.data.tasks || []);
      setStats({
        pending: response.data.pending || 0,
        in_progress: response.data.in_progress || 0,
        submitted: response.data.submitted || 0,
      });
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to load recovery tasks");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchTasks();
    // Fetch org users for approver selection
    api.get("/recovery-tasks/org-users-for-approver")
      .then(res => setOrgUsers(res.data.users || []))
      .catch(() => {});
  }, [fetchTasks, api]);

  const fetchWizardQuestions = async (teamType) => {
    try {
      const response = await api.get(`/recovery-tasks/wizard/questions/${teamType}`);
      setWizardQuestions(response.data.questions || []);
    } catch (error) {
      console.error("Error fetching wizard questions:", error);
      toast.error("Failed to load wizard questions");
    }
  };

  const calculateRecommendation = async () => {
    if (!selectedTask) return;
    
    try {
      const response = await api.post("/recovery-tasks/wizard/calculate-strategy", null, {
        params: {
          team_type: selectedTask.team_type,
          parent_rto_hours: selectedTask.parent_process?.rto_hours || 24,
        },
        data: wizardAnswers,
      });
      setRecommendation(response.data);
      
      // Pre-fill recovery plan with recommendation
      setRecoveryPlan(prev => ({
        ...prev,
        recovery_strategy: {
          ...prev.recovery_strategy,
          strategy_type: response.data.strategy_type,
          target_rto_hours: selectedTask.team_bia_reference?.rto_hours || 24,
          planned_rto_hours: response.data.estimated_rto_hours,
          justification: response.data.reasoning?.join(". ") || "",
          cost_estimate: response.data.cost_indicator || "medium",
        },
        estimated_recovery_hours: response.data.estimated_rto_hours,
      }));
    } catch (error) {
      console.error("Error calculating recommendation:", error);
    }
  };

  const openTaskDetails = (task) => {
    setSelectedTask(task);
    setShowTaskDialog(true);
  };

  const startWizard = async (task) => {
    setSelectedTask(task);
    setWizardStep(1);
    setWizardAnswers({});
    setRecommendation(null);
    setRecoveryPlan({
      recovery_strategy: {
        strategy_type: "",
        target_rto_hours: task.team_bia_reference?.rto_hours || 24,
        planned_rto_hours: 0,
        target_rpo_hours: task.team_bia_reference?.rpo_hours || 4,
        planned_rpo_hours: 0,
        justification: "",
        cost_estimate: "medium",
      },
      recovery_tasks: [],  // New format
      recovery_steps: [],  // Legacy format
      prerequisites: "",
      estimated_recovery_hours: 0,
      resource_requirements: "",
      validation_steps: [],
      notes: "",
      alternate_work_location: "",
      essential_personnel: [],
      communication_plan: "",
    });
    
    // Initialize first task ID
    setNewTask({
      task_id: "T001",
      actual_task: "",
      task_owner: "",
      predecessor_task_id: "",
      estimated_duration: "30 mins",
      estimated_duration_mins: 30,
      notes: "",
    });
    
    await fetchWizardQuestions(task.team_type);
    setShowWizardDialog(true);
  };

  const handleStartTask = async (taskId) => {
    try {
      await api.post(`/recovery-tasks/${taskId}/start`);
      toast.success("Task started - you can now create your recovery plan");
      fetchTasks();
      setShowTaskDialog(false);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to start task"));
    }
  };

  const handleWizardAnswer = (questionId, answer) => {
    setWizardAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  // Add recovery task in new format (Task ID, Predecessor, etc.)
  const addRecoveryTask = () => {
    if (!newTask.actual_task) {
      toast.error("Please enter a task description");
      return;
    }
    
    // Auto-generate Task ID if not provided
    const taskId = newTask.task_id || `T${String(recoveryPlan.recovery_tasks.length + 1).padStart(3, '0')}`;
    
    setRecoveryPlan(prev => ({
      ...prev,
      recovery_tasks: [
        ...prev.recovery_tasks,
        {
          ...newTask,
          task_id: taskId,
          status: "not_started",
          start_time: null,
          completion_time: null,
        }
      ]
    }));
    
    // Reset form with next Task ID
    setNewTask({
      task_id: `T${String(recoveryPlan.recovery_tasks.length + 2).padStart(3, '0')}`,
      actual_task: "",
      task_owner: "",
      predecessor_task_id: taskId,  // Default to previous task
      estimated_duration: "30 mins",
      estimated_duration_mins: 30,
      notes: "",
    });
  };

  const removeRecoveryTask = (index) => {
    setRecoveryPlan(prev => ({
      ...prev,
      recovery_tasks: prev.recovery_tasks.filter((_, i) => i !== index)
    }));
  };

  const addRecoveryStep = () => {
    if (!newStep.action) {
      toast.error("Please enter an action");
      return;
    }
    
    setRecoveryPlan(prev => ({
      ...prev,
      recovery_steps: [
        ...prev.recovery_steps,
        { ...newStep, order: prev.recovery_steps.length + 1 }
      ]
    }));
    setNewStep({ action: "", responsible: "", duration_mins: 30, notes: "" });
  };

  const removeRecoveryStep = (index) => {
    setRecoveryPlan(prev => ({
      ...prev,
      recovery_steps: prev.recovery_steps
        .filter((_, i) => i !== index)
        .map((step, i) => ({ ...step, order: i + 1 }))
    }));
  };

  const addValidationStep = () => {
    if (!newValidation.trim()) return;
    setRecoveryPlan(prev => ({
      ...prev,
      validation_steps: [...prev.validation_steps, newValidation.trim()]
    }));
    setNewValidation("");
  };

  const removeValidationStep = (index) => {
    setRecoveryPlan(prev => ({
      ...prev,
      validation_steps: prev.validation_steps.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitTask = async () => {
    if (!selectedTask || !recoveryPlan.recovery_strategy.strategy_type) {
      toast.error("Please complete the recovery plan");
      return;
    }
    
    // Check for either new format (recovery_tasks) or legacy format (recovery_steps)
    if (recoveryPlan.recovery_tasks.length === 0 && recoveryPlan.recovery_steps.length === 0) {
      toast.error("Please add at least one recovery task");
      return;
    }
    
    // Set plan type based on selected task
    const planToSubmit = {
      ...recoveryPlan,
      plan_type: selectedTask.is_bcp ? "bcp" : "it_drp",
    };
    
    setSubmitting(true);
    try {
      const response = await api.post(`/recovery-tasks/${selectedTask.id}/submit`, {
        recovery_plan: planToSubmit,
        comments: recoveryPlan.notes,
        approver_user_id: selectedApprover?.id || null,
        approver_email: selectedApprover?.email || null,
        approver_name: selectedApprover?.name || null,
      });
      
      toast.success(selectedApprover
        ? `Recovery plan submitted — ${selectedApprover.name} will review`
        : "Recovery plan submitted for review"
      );
      
      if (response.data.rto_warning) {
        toast.warning(response.data.rto_warning, { duration: 5000 });
      }
      
      setShowWizardDialog(false);
      fetchTasks();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to submit recovery plan"));
    } finally {
      setSubmitting(false);
    }
  };

  const getTeamIcon = (teamType) => {
    const Icon = TEAM_ICONS[teamType] || ClipboardList;
    return <Icon className="w-5 h-5" />;
  };

  const getStatusBadge = (status) => {
    return (
      <Badge className={STATUS_COLORS[status] || "bg-slate-100"}>
        {status?.replace("_", " ").toUpperCase()}
      </Badge>
    );
  };

  const isOverdue = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <div className="w-8 h-8 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-recovery-tasks-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-sky-100 text-sky-700">DO Phase</Badge>
            <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">ISO 22301:8.3-8.4</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">My Recovery Tasks</h1>
          <p className="text-slate-600 mt-1">
            Create recovery strategies and plans for dependencies assigned to your team
          </p>
        </div>
        <Button variant="outline" onClick={fetchTasks} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Tasks</p>
                <p className="text-2xl font-bold text-slate-900">{tasks.length}</p>
              </div>
              <ClipboardList className="w-8 h-8 text-sky-700 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className={stats.pending > 0 ? "border-amber-200" : ""}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">In Progress</p>
                <p className="text-2xl font-bold text-blue-600">{stats.in_progress}</p>
              </div>
              <Play className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Submitted</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.submitted}</p>
              </div>
              <Send className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tasks List */}
      <Card data-testid="tasks-list">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-slate-400" />
            Assigned Recovery Tasks
          </CardTitle>
          <CardDescription>
            Tasks from business processes that depend on your team's services
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-2">No recovery tasks assigned</p>
              <p className="text-sm text-slate-400">
                Tasks will appear here when business processes that depend on your team are approved
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className={`p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer ${
                    isOverdue(task.deadline) && task.status === "pending" 
                      ? "border-red-300 bg-red-50" 
                      : "border-slate-200"
                  }`}
                  onClick={() => openTaskDetails(task)}
                  data-testid={`task-${task.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-lg ${TEAM_COLORS[task.team_type] || "bg-slate-100"}`}>
                        {getTeamIcon(task.team_type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{task.title}</h3>
                          {getStatusBadge(task.status)}
                          {task.is_cloud_hosted && (
                            <Badge className="bg-cyan-100 text-cyan-700">
                              <Cloud className="w-3 h-3 mr-1" />
                              {task.cloud_provider?.toUpperCase() || "Cloud"}
                            </Badge>
                          )}
                          {isOverdue(task.deadline) && task.status === "pending" && (
                            <Badge variant="destructive">OVERDUE</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{task.description}</p>
                        <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            Parent: {task.parent_process?.name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Parent RTO: {task.parent_process?.rto_hours}h
                          </span>
                          <span className="flex items-center gap-1 font-medium text-sky-700">
                            Your Target RTO: {task.team_bia_reference?.rto_hours || "N/A"}h
                          </span>
                          {task.deadline && (
                            <span className={`flex items-center gap-1 ${isOverdue(task.deadline) ? "text-red-600" : ""}`}>
                              <AlertTriangle className="w-3 h-3" />
                              Due: {new Date(task.deadline).toLocaleDateString()}
                            </span>
                          )}
                          {task.approver?.name && (
                            <span className="flex items-center gap-1 text-purple-600 font-medium">
                              <CheckCircle2 className="w-3 h-3" />
                              Approver: {task.approver.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {task.status === "pending" && (
                        <Button 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); startWizard(task); }}
                          data-testid={`start-wizard-${task.id}`}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Start
                        </Button>
                      )}
                      {task.status === "in_progress" && (
                        <Button 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); startWizard(task); }}
                          data-testid={`continue-wizard-${task.id}`}
                        >
                          <ArrowRight className="w-4 h-4 mr-1" />
                          Continue
                        </Button>
                      )}
                      {task.status === "revision_required" && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-sky-700"
                          onClick={(e) => { e.stopPropagation(); startWizard(task); }}
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Revise
                        </Button>
                      )}
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task Details Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedTask && getTeamIcon(selectedTask.team_type)}
              {selectedTask?.title}
            </DialogTitle>
            <DialogDescription>
              {selectedTask?.description}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTask && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Parent Process</p>
                  <p className="font-medium">{selectedTask.parent_process?.name}</p>
                  <p className="text-sm text-slate-600">
                    RTO: {selectedTask.parent_process?.rto_hours}h | 
                    RPO: {selectedTask.parent_process?.rpo_hours}h
                  </p>
                </div>
                <div className="p-3 bg-sky-50 rounded-lg border border-sky-200">
                  <p className="text-xs text-sky-700 mb-1">Your Team's Target</p>
                  <p className="font-medium text-sky-700">
                    RTO: {selectedTask.team_bia_reference?.rto_hours || "N/A"}h
                  </p>
                  <p className="text-sm text-sky-700">
                    {selectedTask.team_bia_reference?.source === "team_bia" 
                      ? `From: ${selectedTask.team_bia_reference?.bia_process_name}`
                      : "Inherited from parent process"}
                  </p>
                </div>
              </div>
              
              {selectedTask.requirements && Object.keys(selectedTask.requirements).some(k => selectedTask.requirements[k]) && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm font-medium text-amber-800 mb-2">Requirements</p>
                  <ul className="text-sm text-amber-700 space-y-1">
                    {selectedTask.requirements.servers?.length > 0 && (
                      <li>Servers: {selectedTask.requirements.servers.join(", ")}</li>
                    )}
                    {selectedTask.requirements.storage_gb && (
                      <li>Storage: {selectedTask.requirements.storage_gb} GB</li>
                    )}
                    {selectedTask.requirements.network_requirements && (
                      <li>Network: {selectedTask.requirements.network_requirements}</li>
                    )}
                    {selectedTask.requirements.special_requirements && (
                      <li>Special: {selectedTask.requirements.special_requirements}</li>
                    )}
                    {selectedTask.requirements.location && (
                      <li>Location: {selectedTask.requirements.location}</li>
                    )}
                  </ul>
                </div>
              )}
              
              {selectedTask.vendor_info && (
                <div className="p-3 bg-sky-50 rounded-lg border border-sky-200">
                  <p className="text-sm font-medium text-sky-800 mb-2">Vendor Information</p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-sky-700">
                    <p>Name: {selectedTask.vendor_info.vendor_name}</p>
                    <p>Type: {selectedTask.vendor_info.vendor_type}</p>
                    {selectedTask.vendor_info.contact_email && (
                      <p>Contact: {selectedTask.vendor_info.contact_email}</p>
                    )}
                    <p>Criticality: {selectedTask.vendor_info.criticality}</p>
                  </div>
                </div>
              )}
              
              {selectedTask.review_comments && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-800 mb-1">Reviewer Comments</p>
                  <p className="text-sm text-red-700">{selectedTask.review_comments}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>
              Close
            </Button>
            {selectedTask?.status === "pending" && (
              <Button onClick={() => { setShowTaskDialog(false); startWizard(selectedTask); }}>
                <Play className="w-4 h-4 mr-2" />
                Start Recovery Planning
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TurboTax-Style Wizard Dialog */}
      <Dialog open={showWizardDialog} onOpenChange={setShowWizardDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedTask && getTeamIcon(selectedTask.team_type)}
              Recovery Plan Wizard - {TEAM_NAMES[selectedTask?.team_type]}
            </DialogTitle>
            <DialogDescription>
              Create a recovery strategy for: {selectedTask?.parent_process?.name}
            </DialogDescription>
          </DialogHeader>

          {/* Wizard Progress */}
          <div className="flex items-center justify-between mb-6 px-4">
            {["Questions", "Strategy", "Procedures", "Review"].map((step, index) => {
              const stepNum = index + 1;
              const isActive = wizardStep === stepNum;
              const isCompleted = wizardStep > stepNum;
              return (
                <div key={step} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isActive ? "bg-sky-700 text-white" :
                        isCompleted ? "bg-emerald-500 text-white" : "bg-slate-200"
                      }`}
                    >
                      {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : stepNum}
                    </div>
                    <span className={`text-xs mt-1 ${isActive ? "text-sky-700 font-medium" : "text-slate-500"}`}>
                      {step}
                    </span>
                  </div>
                  {index < 3 && (
                    <div className={`w-20 h-0.5 mx-2 ${isCompleted ? "bg-emerald-300" : "bg-slate-200"}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Step 1: Guided Questions */}
          {wizardStep === 1 && (
            <div className="space-y-6" data-testid="wizard-step-questions">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">Let's assess your recovery capabilities</p>
                    <p className="text-sm text-blue-700">
                      Answer these questions to help us recommend the best recovery strategy for your team.
                      Your target RTO is <strong>{selectedTask?.team_bia_reference?.rto_hours || "N/A"}h</strong>.
                    </p>
                  </div>
                </div>
              </div>

              {wizardQuestions.map((question) => (
                <div key={question.id} className="p-4 border rounded-lg">
                  <h4 className="font-medium text-slate-900 mb-1">{question.title}</h4>
                  <p className="text-sm text-slate-600 mb-3">{question.question}</p>
                  
                  {question.type === "single_choice" && (
                    <div className="space-y-2">
                      {question.options.map((option) => (
                        <div
                          key={option.id}
                          onClick={() => handleWizardAnswer(question.id, option.id)}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            wizardAnswers[question.id] === option.id
                              ? "border-sky-700 bg-sky-50"
                              : "border-slate-200 hover:border-sky-300"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{option.label}</p>
                              {option.help && (
                                <p className="text-xs text-slate-500">{option.help}</p>
                              )}
                            </div>
                            {wizardAnswers[question.id] === option.id && (
                              <CheckCircle2 className="w-5 h-5 text-sky-700" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {question.impact && (
                    <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      {question.impact}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Step 2: Strategy Selection */}
          {wizardStep === 2 && (
            <div className="space-y-6" data-testid="wizard-step-strategy">
              {recommendation && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-start gap-3">
                    <Target className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-emerald-800">Recommended Strategy: {
                        RECOVERY_STRATEGIES.find(s => s.id === recommendation.strategy_type)?.name
                      }</p>
                      <p className="text-sm text-emerald-700">
                        Estimated RTO: {recommendation.estimated_rto_hours}h
                      </p>
                      {recommendation.reasoning?.map((reason, i) => (
                        <p key={i} className="text-xs text-emerald-600 mt-1">• {reason}</p>
                      ))}
                      {recommendation.warnings?.map((warning, i) => (
                        <p key={i} className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {warning}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {RECOVERY_STRATEGIES.map((strategy) => {
                  const Icon = strategy.icon;
                  const isSelected = recoveryPlan.recovery_strategy.strategy_type === strategy.id;
                  return (
                    <div
                      key={strategy.id}
                      onClick={() => setRecoveryPlan(prev => ({
                        ...prev,
                        recovery_strategy: { ...prev.recovery_strategy, strategy_type: strategy.id }
                      }))}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? "border-sky-700 bg-sky-50 ring-2 ring-indigo-200"
                          : "border-slate-200 hover:border-sky-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${strategy.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold">{strategy.name}</h4>
                            <Badge variant="outline">{strategy.rtoRange}</Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{strategy.description}</p>
                          <p className="text-xs text-slate-500 mt-2">Cost: {strategy.cost}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Planned RTO (hours)</Label>
                  <Input
                    type="number"
                    value={recoveryPlan.recovery_strategy.planned_rto_hours}
                    onChange={(e) => setRecoveryPlan(prev => ({
                      ...prev,
                      recovery_strategy: { 
                        ...prev.recovery_strategy, 
                        planned_rto_hours: parseFloat(e.target.value) || 0 
                      },
                      estimated_recovery_hours: parseFloat(e.target.value) || 0
                    }))}
                  />
                  <p className="text-xs text-slate-500">
                    Target: {selectedTask?.team_bia_reference?.rto_hours || "N/A"}h
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Cost Estimate</Label>
                  <Select
                    value={recoveryPlan.recovery_strategy.cost_estimate}
                    onValueChange={(value) => setRecoveryPlan(prev => ({
                      ...prev,
                      recovery_strategy: { ...prev.recovery_strategy, cost_estimate: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="minimal">Minimal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Strategy Justification</Label>
                <Textarea
                  placeholder="Explain why this strategy was chosen..."
                  value={recoveryPlan.recovery_strategy.justification}
                  onChange={(e) => setRecoveryPlan(prev => ({
                    ...prev,
                    recovery_strategy: { ...prev.recovery_strategy, justification: e.target.value }
                  }))}
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Step 3: Recovery Procedures */}
          {wizardStep === 3 && (
            <div className="space-y-6" data-testid="wizard-step-procedures">
              {/* Plan Type Badge */}
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <Badge className={selectedTask?.is_bcp ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}>
                  {selectedTask?.is_bcp ? "BCP - Business Continuity Plan" : "IT-DRP - IT Disaster Recovery Plan"}
                </Badge>
                <span className="text-sm text-slate-600">
                  {selectedTask?.is_bcp 
                    ? "Covers: Facility, Utilities, People" 
                    : "Covers: IT Components Recovery"}
                </span>
              </div>

              <div className="space-y-2">
                <Label>Prerequisites</Label>
                <Textarea
                  placeholder="What needs to be in place before recovery can begin?"
                  value={recoveryPlan.prerequisites}
                  onChange={(e) => setRecoveryPlan(prev => ({ ...prev, prerequisites: e.target.value }))}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Resource Requirements</Label>
                <Textarea
                  placeholder="What resources (people, tools, access) are needed?"
                  value={recoveryPlan.resource_requirements}
                  onChange={(e) => setRecoveryPlan(prev => ({ ...prev, resource_requirements: e.target.value }))}
                  rows={2}
                />
              </div>

              {/* Recovery Plan Tasks - New Format matching the provided template */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" />
                    Recovery Plan
                  </Label>
                  <span className="text-xs text-slate-500">
                    Start Time & Completion Time will be recorded during exercise
                  </span>
                </div>
                
                {recoveryPlan.recovery_tasks.length > 0 && (
                  <RecoveryTaskTable
                    tasks={recoveryPlan.recovery_tasks}
                    onTasksChange={(updated) => setRecoveryPlan(prev => ({ ...prev, recovery_tasks: updated }))}
                    userName={user?.name || ""}
                    showAddForm={true}
                  />
                )}

                {recoveryPlan.recovery_tasks.length === 0 && (
                  <RecoveryTaskTable
                    tasks={[]}
                    onTasksChange={(updated) => setRecoveryPlan(prev => ({ ...prev, recovery_tasks: updated }))}
                    userName={user?.name || ""}
                    emptyMessage="No recovery tasks defined yet. Click 'Add Task' to begin."
                  />
                )}
              </div>

              {/* Validation Steps */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Validation Checklist
                </Label>
                
                {recoveryPlan.validation_steps.length > 0 && (
                  <div className="space-y-2">
                    {recoveryPlan.validation_steps.map((step, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <span className="flex-1 text-sm">{step}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 h-6 w-6"
                          onClick={() => removeValidationStep(index)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Input
                    placeholder="Add validation step..."
                    value={newValidation}
                    onChange={(e) => setNewValidation(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addValidationStep())}
                  />
                  <Button variant="outline" onClick={addValidationStep}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review & Submit */}
          {wizardStep === 4 && (
            <div className="space-y-6" data-testid="wizard-step-review">
              {/* Plan Type Header */}
              <div className="p-3 rounded-lg border-2 border-dashed flex items-center justify-between" 
                   style={{ borderColor: selectedTask?.is_bcp ? '#10b981' : '#3b82f6' }}>
                <div className="flex items-center gap-2">
                  <Badge className={selectedTask?.is_bcp ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}>
                    {selectedTask?.is_bcp ? "BCP" : "IT-DRP"}
                  </Badge>
                  <span className="font-medium">
                    {selectedTask?.is_bcp ? "Business Continuity Plan" : "IT Disaster Recovery Plan"}
                  </span>
                </div>
                <span className="text-sm text-slate-500">
                  {selectedTask?.is_bcp ? "Facility, Utilities, People" : "IT Components"}
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-2">Recovery Strategy</h4>
                  <p className="text-sm">
                    Type: <strong>{RECOVERY_STRATEGIES.find(s => s.id === recoveryPlan.recovery_strategy.strategy_type)?.name}</strong>
                  </p>
                  <p className="text-sm">Planned RTO: <strong>{recoveryPlan.recovery_strategy.planned_rto_hours}h</strong></p>
                  <p className="text-sm">Cost: {recoveryPlan.recovery_strategy.cost_estimate}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-2">Target Alignment</h4>
                  <p className="text-sm">Your Target RTO: <strong>{selectedTask?.team_bia_reference?.rto_hours || "N/A"}h</strong></p>
                  <p className="text-sm">Parent Process RTO: {selectedTask?.parent_process?.rto_hours}h</p>
                  {recoveryPlan.recovery_strategy.planned_rto_hours > (selectedTask?.team_bia_reference?.rto_hours || 24) && (
                    <Badge variant="destructive" className="mt-2">RTO Exceeds Target</Badge>
                  )}
                </div>
              </div>

              {/* Recovery Plan Tasks - New Format */}
              {recoveryPlan.recovery_tasks.length > 0 && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-2">Recovery Plan ({recoveryPlan.recovery_tasks.length} tasks)</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-200">
                          <th className="p-2 text-left">Task ID</th>
                          <th className="p-2 text-left">Task</th>
                          <th className="p-2 text-left">Owner</th>
                          <th className="p-2 text-left">Predecessor</th>
                          <th className="p-2 text-left">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recoveryPlan.recovery_tasks.map((task, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-2 font-mono">{task.task_id}</td>
                            <td className="p-2">{task.actual_task}</td>
                            <td className="p-2">{task.task_owner}</td>
                            <td className="p-2 font-mono">{task.predecessor_task_id || "-"}</td>
                            <td className="p-2">{task.estimated_duration}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    Total time: {recoveryPlan.recovery_tasks.reduce((sum, t) => sum + (t.estimated_duration_mins || 30), 0)} minutes
                    ({(recoveryPlan.recovery_tasks.reduce((sum, t) => sum + (t.estimated_duration_mins || 30), 0) / 60).toFixed(1)} hours)
                  </p>
                </div>
              )}

              {/* Legacy format - for backwards compatibility */}
              {recoveryPlan.recovery_tasks.length === 0 && recoveryPlan.recovery_steps.length > 0 && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-2">Recovery Procedures ({recoveryPlan.recovery_steps.length} steps)</h4>
                  <ol className="list-decimal list-inside text-sm space-y-1">
                    {recoveryPlan.recovery_steps.map((step, i) => (
                      <li key={i}>{step.action} ({step.duration_mins} mins)</li>
                    ))}
                  </ol>
                  <p className="text-sm text-slate-500 mt-2">
                    Total time: {recoveryPlan.recovery_steps.reduce((sum, s) => sum + s.duration_mins, 0)} minutes
                  </p>
                </div>
              )}

              {recoveryPlan.validation_steps.length > 0 && (
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <h4 className="font-medium mb-2 text-emerald-800">Validation Checklist</h4>
                  <ul className="text-sm space-y-1 text-emerald-700">
                    {recoveryPlan.validation_steps.map((step, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="w-3 h-3" />
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea
                  placeholder="Any additional comments for the reviewer..."
                  value={recoveryPlan.notes}
                  onChange={(e) => setRecoveryPlan(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>

              {/* Approver Selection */}
              <div className="p-4 bg-sky-50 rounded-lg border border-sky-200 space-y-3">
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-sky-700" />
                  <Label className="text-indigo-800 font-medium">Designate Your Approver / Manager</Label>
                </div>
                <p className="text-xs text-sky-700">
                  Select the person who will review and approve this plan. This should be your functional manager or department head.
                </p>
                <Select
                  value={selectedApprover?.id || "none"}
                  onValueChange={(val) => {
                    if (val === "none") {
                      setSelectedApprover(null);
                    } else {
                      const u = orgUsers.find(u => u.id === val);
                      setSelectedApprover(u || null);
                    }
                  }}
                >
                  <SelectTrigger data-testid="approver-select">
                    <SelectValue placeholder="Select an approver..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific approver</SelectItem>
                    {orgUsers.filter(u => u.id !== user?.id).map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.email}){u.department ? ` — ${u.department}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedApprover && (
                  <div className="flex items-center gap-2 text-sm text-sky-700 bg-sky-100 p-2 rounded">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Approver: <strong>{selectedApprover.name}</strong> ({selectedApprover.email})</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => {
                if (wizardStep > 1) {
                  setWizardStep(wizardStep - 1);
                } else {
                  setShowWizardDialog(false);
                }
              }}
            >
              {wizardStep === 1 ? "Cancel" : "Back"}
            </Button>
            
            {wizardStep < 4 ? (
              <Button
                onClick={async () => {
                  if (wizardStep === 1) {
                    await calculateRecommendation();
                  }
                  setWizardStep(wizardStep + 1);
                }}
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmitTask}
                disabled={submitting || !recoveryPlan.recovery_strategy.strategy_type || (recoveryPlan.recovery_tasks.length === 0 && recoveryPlan.recovery_steps.length === 0)}
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit for Review
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
